/**
 * RBAC de la UI de monitoreo.
 *
 * OJO: esto es control de UI, no de seguridad. La autorizacion real la hace la
 * API con el ID token de Google (`AUTH_ALLOWED_EMAILS` / `AUTH_ADMIN_EMAILS`).
 * Aca solo escondemos acciones que el usuario no puede ejecutar, para que no
 * choque contra un 403.
 */

export const ROLES = ['viewer', 'operator', 'admin'] as const;

export type Role = (typeof ROLES)[number];

/** Etiquetas visibles de cada rol. */
export const ROLE_LABELS: Readonly<Record<Role, string>> = {
  viewer: 'Lectura',
  operator: 'Operador',
  admin: 'Administrador',
};

export const PERMISSIONS = [
  'messages:read',
  'deadletters:read',
  'deadletters:replay',
  'deadletters:discard',
  'quarantine:read',
  'quarantine:resolve',
  'warehouses:write',
  'subscriptions:write',
  'simulation:picking',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

const VIEWER_PERMISSIONS: readonly Permission[] = [
  'messages:read',
  'deadletters:read',
  'quarantine:read',
];

const OPERATOR_PERMISSIONS: readonly Permission[] = [
  ...VIEWER_PERMISSIONS,
  'deadletters:replay',
  'deadletters:discard',
  'quarantine:resolve',
  // Descubrir bodegas en SAP, crearlas en Janis y decidir cuáles publican stock.
  'warehouses:write',
  // Entregar avisos del WMS simulado: terminan en escrituras reales en Janis.
  'simulation:picking',
];

const ADMIN_PERMISSIONS: readonly Permission[] = [...OPERATOR_PERMISSIONS, 'subscriptions:write'];

const PERMISSIONS_BY_ROLE: Readonly<Record<Role, readonly Permission[]>> = {
  viewer: VIEWER_PERMISSIONS,
  operator: OPERATOR_PERMISSIONS,
  admin: ADMIN_PERMISSIONS,
};

/** Devuelve true si el rol tiene el permiso pedido. */
export function can(role: Role | null | undefined, permission: Permission): boolean {
  if (!role) {
    return false;
  }
  return PERMISSIONS_BY_ROLE[role].includes(permission);
}

/** Valida que un string venga de la API como un rol conocido. */
export function isRole(value: unknown): value is Role {
  return typeof value === 'string' && (ROLES as readonly string[]).includes(value);
}
